import { useState } from 'react'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  DefaultSubtitle,
  ReviewCardContainer,
} from 'src/components/application/defaultComponents'
import ApplicationPrebuiltFooter from 'src/components/application/PrebuiltBodyFooter'
import { useApplication } from 'src/context/application'
import { useNavigate, createSearchParams, useLocation } from 'react-router-dom'
import Button from 'src/components/basic/Button'
import { IoMdCheckmark } from 'react-icons/io'
import ApplicationData, { ReviewData, JudgeInsuranceData, ClaimData } from 'src/dtos/applicationData'
import StandardInput from 'src/components/basic/StandardInput'
import FilePicker from 'src/components/basic/FilePicker'
import { uploadFiles } from 'src/services/apiClient'
import { handleYesNoAnswers } from 'src/utils/formDataManipulation'
import { TbFileCheck } from 'react-icons/tb'
import { numberToCurrency } from 'src/utils/numberFormat'
import {FILE_SIZE_LABEL} from "../../../../utils/constants";

const Insurance = ({screenshotMode = false}) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { application, saveApplication } = useApplication()
  const [review, setReview] = useState<ReviewData>(application?.judgeApplicationData?.insuranceSection?.review ?? {})
  const [showNotesInput, setShowNotesInput] = useState(!!application?.judgeApplicationData?.insuranceSection?.review?.note)
  const [showFilePicker, setShowFilePicker] = useState(!!application?.judgeApplicationData?.insuranceSection?.review?.files)

  const handleEditClick = () => {
    navigate({
      pathname: '/judge/insurance/last-insurances',
      search: createSearchParams({ review: 'insurance' }).toString()
    })
  }

  const updatedApplication = (): ApplicationData => ({
    ...application,
    stoppedAt: pathname,
    judgeApplicationData: {
      ...application?.judgeApplicationData,
      insuranceSection: {
        ...application?.judgeApplicationData?.insuranceSection,
        review
      }
    }
  })

  const handleSaveApplication = async () => {
    try {
      await saveApplication(updatedApplication())
    } catch (_) { }
  }

  const produceApplicationData = (key: keyof ReviewData, value: any) => ({
    ...application,
    stoppedAt: pathname,
    judgeApplicationData: {
      ...application?.judgeApplicationData,
      insuranceSection: {
        ...application?.judgeApplicationData?.insuranceSection,
        review: {
          ...application?.judgeApplicationData?.insuranceSection?.review,
          [key]: value
        }
      }
    }
  })

  const handleRemoveNote = async () => {
    const removedNoteApplication = produceApplicationData('note', undefined)
    setReview(prev => ({ ...prev, note: undefined }))
    setShowNotesInput(false)
    await saveApplication(removedNoteApplication)
  }

  const handleUploadFiles = async (files: File[]) => {
    if(files.length > 0) {
      const uploadedFiles = await uploadFiles(files)

      const updatedFiles = [...(review?.files ?? []), ...(uploadedFiles ?? [])]
  
      const applicationFileAttached = produceApplicationData('files', updatedFiles)
      
      setReview(prev => ({ ...prev, files: updatedFiles }))
      await saveApplication(applicationFileAttached)
    } else {
      setReview(prev => ({ ...prev, files: undefined }))
      const applicationFileAttached = produceApplicationData('files', undefined)
      await saveApplication(applicationFileAttached)
    }
  }

  const insuranceSection = application?.judgeApplicationData?.insuranceSection
  const claims = insuranceSection?.claims

  const hasClaims = insuranceSection?.pastLiability?.hasClaimsAsserted === true ||
    insuranceSection?.pastLiability?.hasActsToBaseClaim === true

  const willRenderClaims = hasClaims && claims && claims.length > 0

  const InsuranceComponent = ({ insurance, index }: { insurance: JudgeInsuranceData, index: number }) => (
    <section>
      <article>
        <strong>{index === 0 ? 'Most Recent Insurance' : 'Prior Insurance'}</strong>
        <span>
          <h6 style={{ marginTop: 0 }}>Insurance Company: </h6>
          {insurance?.insuranceCompany ?? 'N/A'}
        </span>
        <span>
          <h6>Limits of Liability: </h6>
          {insurance?.liabilityLimits ?? 'N/A'}
        </span>
        <span>
          <h6>Effective Dates</h6>
          <span style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}><h6>From: </h6>{insurance?.effectiveDateFrom ?? 'N/A'}</span>
          <span style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}><h6>To: </h6>{insurance?.effectiveDateTo ?? 'N/A'}</span>
        </span>
      </article>
    </section>
  )

  const ClaimComponent = ({ claim, index }: { claim: ClaimData, index: number }) => (
    <section>
      <h5>CLAIM {index + 1}</h5>
      <article>
        <strong>Firm Name</strong>
        <span>{claim?.firmName ?? 'N/A'}</span>
      </article>
      <article>
        <strong>Claimant’s Full Name</strong>
        <span>{claim?.hasClaimant === false ? "I'm not sure" : (claim?.claimantFullName ?? 'N/A')}</span>
      </article>
      <article>
        <strong>Claim Type</strong>
        <span>{claim?.claimType ?? 'N/A'}</span>
      </article>
      <article>
        <strong>Date of Claim</strong>
        <span>{claim?.hasClaimDate === false ? "I'm not sure" : (claim?.claimDate ?? 'N/A')}</span>
      </article>
      <article>
        <strong>Date of Alleged Error</strong>
        <span>{claim?.hasAllegedErrorDate === false ? "I'm not sure" : (claim?.allegedErrorDate ?? 'N/A')}</span>
      </article>
      <article>
        <strong>Is this claim closed?</strong>
        <span>{claim?.isClaimClosed ?? 'N/A'}</span>
        {claim?.isClaimClosed === 'Yes' ? (
          <>
            <span>
              <h6>Indicate how the claim was closed: </h6>
              {claim?.closingType ?? 'N/A'}
            </span>
            <span>
              <h6>Total loss paid including deductibles: </h6>
              {claim?.totalLossPaid ? numberToCurrency(claim?.totalLossPaid) : 'N/A'}
            </span>
            <span>
              <h6>Defense cost paid: </h6>
              {claim?.defenseCostPaid ? numberToCurrency(claim?.defenseCostPaid) : 'N/A'}
            </span>
          </>
        ) : claim?.isClaimClosed === 'No' ? (
          <>
            <span>
              <h6>Claimant’s settlement demand: </h6>
              {claim?.claimantsSettlementDemand ? numberToCurrency(claim?.claimantsSettlementDemand) : 'N/A'}
            </span>
            <span>
              <h6>Defendant’s offer for settlement: </h6>
              {claim?.defendantsSettlementOffer ? numberToCurrency(claim?.defendantsSettlementOffer) : 'N/A'}
            </span>
            <span>
              <h6>Insurer’s loss reserve: </h6>
              {claim?.insurersLossReserve ? numberToCurrency(claim?.insurersLossReserve) : 'N/A'}
            </span>
            <span>
              <h6>Deductible: </h6>
              {claim?.closingCaseDeductible ? numberToCurrency(claim?.closingCaseDeductible) : 'N/A'}
            </span>
            <span>
              <h6>Is claim in suit?: </h6>
              {claim?.isClaimInSuit ?? 'N/A'}
            </span>
            {claim?.isClaimInSuit === 'Yes' ? (
              <span>
                <h6>Amount asked in summons: </h6>
                {claim?.amountAskedSummons ? numberToCurrency(claim?.amountAskedSummons) : 'N/A'}
              </span>
            ) : null}
            <span>
              <h6>Total loss pain including deductibles: </h6>
              {claim?.totalLossPain ? numberToCurrency(claim?.totalLossPain) : 'N/A'}
            </span>
          </>
        ) : null}
      </article>
      <article>
        <strong>Name of Insurer</strong>
        <span>{claim?.hasInsurer === false ? "I'm not sure" : (claim?.insurerName ?? 'N/A')}</span>
      </article>
      <article>
        <strong>Description of Claim</strong>
        <span>
          <h6>Alleged act, error, or omission upon which claimant bases claim:</h6>
        </span>
        <span>{claim?.hasClaimDescription === false ? "I'm not sure" : (claim?.claimDescription ?? 'N/A')}</span>
        <span>
          <h6>Description of case and events, including area of practice out of which the claim arose:</h6>
        </span>
        <span>{claim?.hasCaseDescription === false ? "I'm not sure" : (claim?.caseDescription ?? 'N/A')}</span>
        <span>
          <h6>Description of the type and extent of injury or damage allegedly sustained:</h6>
        </span>
        <span>{claim?.hasDamageDescription === false ? "I'm not sure" : (claim?.damageDescription ?? 'N/A')}</span>
        <span>
          <h6>Upload any applicable claim information:</h6>
        </span>
        {claim?.claimFile?.map(file => (
          <span key={file?.id} style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
            {!!file?.fileName ? <TbFileCheck /> : null}
            {file?.fileName ?? 'N/A'}
          </span>
        ))}
      </article>
    </section>
  )

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='80%'>
        <DefaultTitle>Almost done! Please review your last insurance information before you sign.</DefaultTitle>
        <DefaultSubtitle>If any answers are innacurate or incomplete you can use the edit button to change them.</DefaultSubtitle>
        <ReviewCardContainer id='insuranceData'>
          <header>
            <div>
              <h4>Last Insurance</h4>
              {!screenshotMode && (
                <h6>
                  <IoMdCheckmark />
                  Complete
                </h6>
              )}
            </div>
            {!screenshotMode && (
              <div>
                <Button style={{ padding: '8px 46px' }} onClick={handleEditClick}>Edit Section</Button>
              </div>
            )}
          </header>
          <main className='firm'>
            <div className='column'>
              <section>
                <h5>INSURANCE HISTORY</h5>
                <article>
                  <strong>Has applicant been insured within the last 4 years?</strong>
                  <span>{handleYesNoAnswers(insuranceSection?.hasBeenInsuredRecently)}</span>
                </article>
              </section>
            </div>
          </main>
          {insuranceSection?.hasBeenInsuredRecently === true ? (
            <>
              <h5 style={{marginTop: '32px'}}>PREVIOUS INSURANCES</h5>
              <main className='firm'>
                <div className='column'>
                  {insuranceSection?.recentInsurances?.map((insurance, index) => {
                    if (index % 2 === 0) return <InsuranceComponent key={index} insurance={insurance} index={index} />
                    return null
                  })}
                </div>
                <div className='column'>
                  {insuranceSection?.recentInsurances?.map((insurance, index) => {
                    if (index % 2 !== 0) return <InsuranceComponent key={index} insurance={insurance} index={index} />
                    return null
                  })}
                </div>
              </main>
            </>
          ) : null}
          <h5 style={{marginTop: '32px'}}>PAST LIABILITY</h5>
          <main className='firm'>
            <div className="column">
              <section>
                <article>
                  <strong>Has the applicant ever had similar insurance canceled, declined or non-renewed?</strong>
                  <span>{handleYesNoAnswers(insuranceSection?.pastLiability?.hasInsuranceDeclined)}</span>
                  {insuranceSection?.pastLiability?.hasInsuranceDeclined === true ? (
                    <span>
                      {insuranceSection?.pastLiability?.insuranceDeclinedDetails ?? 'N/A'}
                    </span>
                  ) : null}
                </article>
                <article>
                  <strong>
                  Has the applicant ever been reprimanded or refused admission to practice before any court or administrative agency or otherwise been disbarred or had his or her rights to practice suspended (including voluntary suspension)?
                  </strong>
                  <span>{handleYesNoAnswers(insuranceSection?.pastLiability?.hasBeenRefused)}</span>
                  {insuranceSection?.pastLiability?.hasBeenRefused === true ? (
                    <span>{insuranceSection?.pastLiability?.refusedDetails ?? 'N/A'}</span>
                  ) : null}
                </article>
                <article>
                  <strong>Does the applicant know of any circumstance, act, error or omission that might form the basis of a claim against the applicant?</strong>
                  <span>{handleYesNoAnswers(insuranceSection?.pastLiability?.hasActsToBaseClaim)}</span>
                </article>
              </section>
            </div>
            <div className="column">
              <section>
                <article>
                  <strong>
                  Has the applicant ever had any professional liability claims asserted or action filed against him or her, or has a claim been paid on behalf of the applicant?
                  </strong>
                  <span>{handleYesNoAnswers(insuranceSection?.pastLiability?.hasClaimsAsserted)}</span>
                </article>
                <article>
                  <strong>Has the applicant ever been reprimanded or had his or her rights to serve as a judge suspended (including voluntary suspension)?</strong>
                  <span>{handleYesNoAnswers(insuranceSection?.pastLiability?.hasBeenSuspended)}</span>
                  {insuranceSection?.pastLiability?.hasBeenSuspended === true ? (
                    <span>{insuranceSection?.pastLiability?.suspendedDetails ?? 'N/A'}</span>
                  ) : null}
                </article>
              </section>
            </div>
          </main>
          {willRenderClaims ? (
            <main className="firm">
              <div className="column">
                {claims?.map((claim, index) => {
                  if(index % 2 === 0) return <ClaimComponent claim={claim} index={index} key={index} />
                  return null
                })}
              </div>
              <div className="column">
                {claims?.map((claim, index) => {
                  if(index % 2 !== 0) return <ClaimComponent claim={claim} index={index} key={index} />
                  return null
                })}
              </div>
            </main>
          ) : null}
          {!screenshotMode && (
            <footer>
              {showNotesInput ? (
                <div className="review-footer-note-container">
                  <StandardInput
                    label=""
                    rows={15}
                    value={review?.note ?? ''}
                    onChange={e => setReview(prev => ({ ...prev, note: e.target.value }))}
                  />
                  <div className="review-footer-note-buttons">
                    {!!insuranceSection?.review?.note ? (
                      <Button
                        variant="error"
                        onClick={handleRemoveNote}
                      >
                        Remove note
                      </Button>
                    ) : (
                      <>
                        <Button
                          className="review-button-small-width"
                          onClick={handleSaveApplication}
                        >
                          Save details
                        </Button>
                        <Button
                          variant="error"
                          className="review-button-small-width"
                          onClick={() => {
                            setReview(prev => ({ ...prev, note: undefined }))
                            setShowNotesInput(false)
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="review-footer-button-container">
                  <Button
                    variant="secondary"
                    onClick={() => setShowNotesInput(true)}
                  >
                    Add a note
                  </Button>
                </div>
              )}
              {showFilePicker ? (
                <div className='review-footer-filepicker-container'>
                  <FilePicker 
                    title={FILE_SIZE_LABEL}
                    onFilesSelect={handleUploadFiles}
                    selectedFiles={review?.files ?? []}
                    accept='.doc,.docx,application/msword, application/pdf'
                    limit={100}
                  />
                </div>
              ) : (
                <div className="review-footer-button-container">
                  <Button
                    variant="secondary"
                    onClick={() => setShowFilePicker(true)}
                  >
                    Attach a document
                  </Button>
                </div>
              )}
            </footer>
          )}
        </ReviewCardContainer>
      </ApplicationBodyContent>
      <ApplicationPrebuiltFooter
        goBackRoute="/judge/review/basic"
        nextRoute="/judge/sign/signature"
        updatedApplication={updatedApplication()}
      />
    </ApplicationBodyContainer>
  )
}

export default Insurance